import { useTranslation } from 'react-i18next'
import type { Participant, Team } from '@brochure/shared'

type ParticipantsListProps = {
  participants: Pick<Participant, 'id' | 'name' | 'teamId'>[]
  teams?: Team[]
}

/**
 * Tabella degli iscritti, con la stessa carta della classifica: numero d'iscrizione, nome e, dopo il sorteggio,
 * la squadra. Una riga per persona, così anche con tanti iscritti si legge dall'alto in basso senza cercare.
 */
export function ParticipantsList({ participants, teams }: ParticipantsListProps) {
  const { t } = useTranslation()
  const hasTeams = teams !== undefined && teams.length > 0
  const teamName = (teamId: string | null) => (teamId ? teams?.find((team) => team.id === teamId)?.name : undefined)

  return (
    <section aria-labelledby="participants-title">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="participants-title" className="font-display font-semibold text-3xl text-ink">
          {t('zambePong.counts.participants', { count: participants.length })}
        </h2>
        {hasTeams && <p className="font-body text-sm text-ink-soft">{t('zambePong.counts.teams', { count: teams.length })}</p>}
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-ink/10 bg-paper shadow-card">
        <table className="w-full font-body text-sm">
          <thead>
            <tr className="border-b border-sun/45 bg-paper-deep/60 text-left text-xs tracking-[0.02em] text-ink-soft">
              <th scope="col" className="w-12 py-2.5 pl-4 font-semibold">
                #
              </th>
              <th scope="col" className="px-2 py-2.5 font-semibold">
                {t('zambePong.participantsTable.name')}
              </th>
              {hasTeams && (
                <th scope="col" className="py-2.5 pr-4 pl-2 font-semibold">
                  {t('zambePong.standings.team')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {participants.length === 0 && (
              <tr>
                <td colSpan={hasTeams ? 3 : 2} className="px-4 py-6 text-center text-ink-soft">
                  {t('zambePong.participantsTable.empty')}
                </td>
              </tr>
            )}
            {participants.map((participant, index) => (
              <tr key={participant.id} className="border-b border-line/70 last:border-0">
                <td className="py-2.5 pl-4 tabular-nums text-ink-soft">{index + 1}</td>
                <td className="max-w-0 truncate px-2 py-2.5 font-medium text-ink">{participant.name}</td>
                {hasTeams && <td className="max-w-0 truncate py-2.5 pr-4 pl-2 text-ink-soft">{teamName(participant.teamId) ?? '–'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
